import { Component, computed, inject, input } from '@angular/core';
import { FileUploadedProgressPipe } from '../../pipes/file-uploaded-progress';
import {
  CancelFileUploadJob,
  RetryFileUploadJob,
  DmsIntegrationStateSelectors,
  DATA_SELECTOR_INJECTION_TOKEN,
  FileSizePipe,
  Logger,
  UploadFileStateRecord,
  SharedModule,
  UploadFileStatus,
  UploadType,
} from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { DecimalPipe, NgClass } from '@angular/common';
import { ClientRequestDocumentFileDisplayStatus } from '../../../../../models';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FileItemIconComponent } from '../file-item-icon/file-item-icon.component';

type ComponentStateTypes = 'hold' | 'waiting' | 'uploading' | 'error' | 'categorizing' | 'linking' | 'uploaded' | 'categorization-timeout';
type ComponentModeTypes = 'client-request' | 'request-link';
const CANCELABLE_STATE_TYPES: ComponentStateTypes[] = ['hold', 'waiting', 'uploading', 'error'];

interface FileUploadJobViewState extends UploadFileStateRecord {
  fileName: string;
  progress: number;
}

@Component({
  selector: 'awn-file-upload-job-item',
  imports: [FileUploadedProgressPipe, SharedModule, NgClass, MatProgressBar, DecimalPipe, FileSizePipe, FileItemIconComponent],
  templateUrl: './file-upload-job-item.component.html',
  styleUrl: './file-upload-job-item.component.scss',
  providers: [{ provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'file-upload-job-item' }],
})
export class FileUploadJobItemComponent {
  // Injected properties
  private store = inject(Store);
  private logger = inject(Logger);
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  // Input Signals
  public jobId = input<string>();
  public compact = input<boolean>(false);
  public mode = input<ComponentModeTypes>();

  // Store Data
  public fileJob = computed(() => {
    const storeData = this.store.selectSignal(DmsIntegrationStateSelectors.getUploadFileJobByJobId(this.jobId()))();
    return this.calculateJobViewProperties(storeData);
  });

  // Computed
  public status = computed(() => this.fileJob().status);
  public viewMode = computed(() => this.calculateComponentMode());
  public canDelete = computed(() => CANCELABLE_STATE_TYPES.includes(this.viewMode()));

  // Methods

  cancelUploadJob(jobId: string): void {
    if (this.status() !== UploadFileStatus.LOADED) {
      this.store.dispatch(new CancelFileUploadJob(jobId));
    }
  }

  retryFileUpload(jobId: string): void {
    if (this.status() !== UploadFileStatus.LOADED) {
      this.store.dispatch(new RetryFileUploadJob(jobId));
    }
  }

  calculateJobViewProperties(storeJob: UploadFileStateRecord): FileUploadJobViewState | null {
    if (!storeJob || !storeJob.data) {
      return null;
    }
    const progress = storeJob?.bytesTotal ? Math.round((100 * storeJob.bytesLoaded) / storeJob.bytesTotal) : 0;
    return {
      ...storeJob,
      fileName: storeJob.data?.file?.name || storeJob.data?.fileName,
      progress,
    };
  }

  calculateComponentMode(): ComponentStateTypes {
    const job = this.fileJob();
    if (!job || job.status === UploadFileStatus.DEFAULT) {
      return 'waiting';
    }
    if (job.status === UploadFileStatus.HOLD) {
      return 'hold';
    }
    if (job.status === UploadFileStatus.LOADING) {
      return 'uploading';
    }
    if (job.status === UploadFileStatus.ERROR) {
      return 'error';
    }
    if (job.status === UploadFileStatus.LOADED) {
      // Request-link and single-call CRL replace upload AND link server-side in one
      // request, so LOADED is terminal for them — no categorization follow-up.
      if (
        job.entityData?.uploadType === UploadType.REQUEST_LINK ||
        job.entityData?.uploadType === UploadType.CLIENT_REQUEST_REPLACE
      ) {
        return 'uploaded';
      }
      // Everything else (incl. bulk CLIENT_REQUEST_SUBMISSION) respects data.status:
      // a bulk drop spins as 'categorizing' until the categorization subscription
      // settles it to a terminal status ('uploaded' — the card owns the
      // uncategorized surface).
      if (job.data.status === ClientRequestDocumentFileDisplayStatus.CATEGORIZING) {
        return 'categorizing';
      }
      if (job.data.status === ClientRequestDocumentFileDisplayStatus.CATEGORIZATION_TIMEOUT) {
        return 'categorization-timeout';
      }
      if (
        job.data.status === ClientRequestDocumentFileDisplayStatus.CATEGORIZED ||
        job.data.status === ClientRequestDocumentFileDisplayStatus.UNCATEGORIZED
      ) {
        return 'uploaded';
      }
      return 'linking';
    }
    this.logger.error('FileUploadJobItemComponent :: incorrect state detected.');
    return 'waiting';
  }
}
